import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Plus, Trash2, MapPin, Pencil, Download, Upload, FileSpreadsheet } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PREDEFINED_MUNICIPALITIES = [
  { id: 'ciudad_real', name: 'Ciudad Real', color: 'bg-purple-100 text-purple-700 border-purple-400' },
  { id: 'bolanos', name: 'Bolaños de Calatrava', color: 'bg-blue-100 text-blue-700 border-blue-400' },
  { id: 'almagro', name: 'Almagro', color: 'bg-emerald-100 text-emerald-700 border-emerald-400' },
  { id: 'pozuelo', name: 'Pozuelo de Calatrava', color: 'bg-amber-100 text-amber-700 border-amber-400' }
];

const HolidayManagement = ({ token, user }) => {
  const [holidays, setHolidays] = useState([]);
  const [customHolidays, setCustomHolidays] = useState([]);
  const [customMunicipalities, setCustomMunicipalities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [useCustomMunicipality, setUseCustomMunicipality] = useState(false);
  const [customMunicipalityName, setCustomMunicipalityName] = useState('');
  const [holidayScope, setHolidayScope] = useState('local'); // 'global' or 'local'
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const [newHoliday, setNewHoliday] = useState({
    date: '',
    name: '',
    municipality: 'ciudad_real',
    holiday_type: 'local'
  });

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    if (token) {
      fetchHolidays();
      fetchCustomHolidays();
      fetchMunicipalities();
    }
  }, [token, selectedYear]);

  const fetchHolidays = async () => {
    try {
      const response = await axios.get(`${API}/holidays/year/${selectedYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHolidays(response.data.holidays || {});
    } catch (error) {
      console.error('Error fetching holidays:', error);
    }
  };

  const fetchCustomHolidays = async () => {
    try {
      const response = await axios.get(`${API}/holidays/custom?year=${selectedYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomHolidays(response.data || []);
    } catch (error) {
      console.error('Error fetching custom holidays:', error);
    }
  };

  const fetchMunicipalities = async () => {
    try {
      const response = await axios.get(`${API}/holidays/municipalities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomMunicipalities(response.data.custom || []);
    } catch (error) {
      console.error('Error fetching municipalities:', error);
    }
  };

  const handleAddHoliday = async (e) => {
    e.preventDefault();
    
    // Determine the municipality value based on scope
    let municipalityValue;
    let holidayType;
    
    if (holidayScope === 'global') {
      municipalityValue = 'todos';
      holidayType = 'global';
    } else {
      municipalityValue = useCustomMunicipality 
        ? customMunicipalityName.trim() 
        : newHoliday.municipality;
      holidayType = 'local';
    }
    
    if (!newHoliday.date || !newHoliday.name) {
      toast.error('Por favor completa la fecha y el nombre');
      return;
    }
    
    if (holidayScope === 'local' && !municipalityValue) {
      toast.error('Por favor selecciona una población');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/holidays/custom`, {
        ...newHoliday,
        municipality: municipalityValue,
        holiday_type: holidayType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Festivo añadido correctamente');
      resetForm();
      setShowAddForm(false);
      fetchCustomHolidays();
      fetchHolidays();
      fetchMunicipalities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al añadir festivo');
    } finally {
      setLoading(false);
    }
  };

  const handleEditHoliday = async (e) => {
    e.preventDefault();
    if (!editingHoliday) return;

    // Determine the municipality value
    const municipalityValue = useCustomMunicipality 
      ? customMunicipalityName.trim() 
      : editingHoliday.municipality;

    if (!editingHoliday.date || !editingHoliday.name) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setLoading(true);
    try {
      if (editingHoliday.is_custom) {
        // Update custom holiday
        await axios.put(`${API}/holidays/custom/${editingHoliday.id}`, {
          date: editingHoliday.date,
          name: editingHoliday.name,
          municipality: municipalityValue
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Update predefined holiday (create override)
        await axios.put(`${API}/holidays/predefined/${editingHoliday.original_date || editingHoliday.date}`, {
          original_date: editingHoliday.original_date || editingHoliday.date,
          new_date: editingHoliday.date,
          new_name: editingHoliday.name,
          deleted: false
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      toast.success('Festivo actualizado correctamente');
      resetForm();
      setShowEditForm(false);
      setEditingHoliday(null);
      fetchHolidays();
      fetchCustomHolidays();
      fetchMunicipalities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar festivo');
    } finally {
      setLoading(false);
    }
  };

  // Download Excel template
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/holidays/template/download?year=${selectedYear + 1}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `plantilla_festivos_${selectedYear + 1}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Plantilla descargada correctamente');
    } catch (error) {
      toast.error('Error al descargar la plantilla');
    }
  };

  // Import holidays from Excel
  const handleImportFile = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setLoading(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/holidays/import`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResult(response.data);
      toast.success(`Importados ${response.data.inserted} festivos correctamente`);
      
      // Refresh holidays list
      fetchHolidays();
      fetchCustomHolidays();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al importar';
      toast.error(errorMsg);
      setImportResult({ error: errorMsg });
    } finally {
      setLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const openEditModal = (holiday) => {
    const isCustom = holiday.is_custom || holiday.type === 'custom';
    const isPredefined = !isCustom && PREDEFINED_MUNICIPALITIES.some(m => m.id === holiday.municipality);
    
    setEditingHoliday({
      id: holiday.id,
      date: holiday.date,
      name: holiday.name,
      municipality: isCustom ? (isPredefined ? holiday.municipality : 'custom') : null,
      is_custom: isCustom,
      original_date: holiday.original_date || holiday.date,
      type: holiday.type
    });
    
    if (isCustom) {
      setUseCustomMunicipality(!isPredefined && holiday.municipality !== 'ciudad_real' && holiday.municipality !== 'bolanos' && holiday.municipality !== 'almagro' && holiday.municipality !== 'pozuelo');
      setCustomMunicipalityName(!isPredefined ? holiday.municipality : '');
    } else {
      setUseCustomMunicipality(false);
      setCustomMunicipalityName('');
    }
    setShowEditForm(true);
  };

  const handleDeleteHoliday = async (holiday) => {
    setLoading(true);
    try {
      if (holiday.is_custom || holiday.type === 'custom') {
        // Delete custom holiday
        await axios.delete(`${API}/holidays/custom/${holiday.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Delete predefined holiday (create override with deleted=true)
        await axios.delete(`${API}/holidays/predefined/${holiday.original_date || holiday.date}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      toast.success('Festivo eliminado correctamente');
      setDeleteConfirm(null);
      fetchHolidays();
      fetchCustomHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar festivo');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setNewHoliday({ date: '', name: '', municipality: 'ciudad_real', holiday_type: 'local' });
    setUseCustomMunicipality(false);
    setCustomMunicipalityName('');
    setHolidayScope('local');
  };

  const getHolidayTypeLabel = (type, municipality, holidayType) => {
    // Check if it's a global custom holiday
    if (type === 'custom' && (holidayType === 'global' || municipality === 'todos')) {
      return { label: 'Global (Todos)', class: 'bg-indigo-100 text-indigo-700' };
    }
    
    const types = {
      national: { label: 'Nacional', class: 'bg-red-100 text-red-700' },
      regional: { label: 'Autonómico', class: 'bg-orange-100 text-orange-700' },
      local_ciudad_real: { label: 'Ciudad Real', class: 'bg-purple-100 text-purple-700' },
      local_bolanos: { label: 'Bolaños', class: 'bg-blue-100 text-blue-700' },
      local_almagro: { label: 'Almagro', class: 'bg-emerald-100 text-emerald-700' },
      local_pozuelo: { label: 'Pozuelo', class: 'bg-amber-100 text-amber-700' },
      custom: { label: 'Personalizado', class: 'bg-slate-100 text-slate-700' },
      custom_global: { label: 'Global (Todos)', class: 'bg-indigo-100 text-indigo-700' }
    };
    
    // For custom type, check if it's a known municipality
    if (type === 'custom' && municipality) {
      const predefined = PREDEFINED_MUNICIPALITIES.find(m => m.id === municipality);
      if (predefined) {
        return { label: predefined.name, class: predefined.color };
      }
      // Check custom municipalities
      const custom = customMunicipalities.find(m => m.id === municipality);
      if (custom && custom.color) {
        return { 
          label: municipality, 
          class: `${custom.color.bg} ${custom.color.text}` 
        };
      }
      return { label: municipality, class: 'bg-slate-100 text-slate-700' };
    }
    
    return types[type] || types.custom;
  };

  const getMunicipalityDisplay = (municipality) => {
    const predefined = PREDEFINED_MUNICIPALITIES.find(m => m.id === municipality);
    if (predefined) return predefined.name;
    return municipality || 'Sin municipio';
  };

  // Get all municipalities for the selector (predefined + custom)
  const getAllMunicipalities = () => {
    const all = [...PREDEFINED_MUNICIPALITIES];
    customMunicipalities.forEach(cm => {
      if (!all.find(m => m.id === cm.id)) {
        all.push({
          id: cm.id,
          name: cm.name,
          color: cm.color ? `${cm.color.bg} ${cm.color.text} ${cm.color.border}` : 'bg-slate-100 text-slate-700 border-slate-400'
        });
      }
    });
    return all;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };

  // Convert holidays object to array for display
  const holidaysArray = Object.entries(holidays).map(([date, info]) => ({
    date,
    ...info
  })).sort((a, b) => new Date(a.date) - new Date(b.date));

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Gestión de Festivos
            </span>
            <div className="flex items-center gap-4">
              <Select 
                value={selectedYear.toString()} 
                onValueChange={(value) => setSelectedYear(parseInt(value))}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2025, 2026, 2027, 2028].map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isAdmin && (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleDownloadTemplate} title="Descargar plantilla Excel">
                    <Download className="h-4 w-4 mr-2" />
                    Plantilla
                  </Button>
                  <Button variant="outline" onClick={() => setShowImportDialog(true)} title="Importar desde Excel">
                    <Upload className="h-4 w-4 mr-2" />
                    Importar
                  </Button>
                  <Button onClick={() => setShowAddForm(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Añadir Festivo
                  </Button>
                </div>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 text-sm">
            <span className="font-medium">Leyenda:</span>
            <Badge className="bg-red-100 text-red-700">Nacional</Badge>
            <Badge className="bg-orange-100 text-orange-700">Autonómico CLM</Badge>
            <Badge className="bg-indigo-100 text-indigo-700">Global (Todos)</Badge>
            <Badge className="bg-purple-100 text-purple-700">Ciudad Real</Badge>
            <Badge className="bg-blue-100 text-blue-700">Bolaños</Badge>
            <Badge className="bg-emerald-100 text-emerald-700">Almagro</Badge>
            <Badge className="bg-amber-100 text-amber-700">Pozuelo</Badge>
            <Badge className="bg-slate-100 text-slate-700">Personalizado</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Holidays Table */}
      <Card>
        <CardHeader>
          <CardTitle>Festivos {selectedYear}</CardTitle>
        </CardHeader>
        <CardContent>
          {holidaysArray.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Festivo</TableHead>
                  <TableHead>Tipo</TableHead>
                  {isAdmin && <TableHead className="text-right">Acciones</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {holidaysArray.map((holiday) => {
                  const typeInfo = getHolidayTypeLabel(holiday.type, holiday.municipality);
                  const isCustom = holiday.type === 'custom' || holiday.is_custom;
                  return (
                    <TableRow key={holiday.date}>
                      <TableCell className="font-medium">
                        {formatDate(holiday.date)}
                      </TableCell>
                      <TableCell>{holiday.name}</TableCell>
                      <TableCell>
                        <Badge className={typeInfo.class}>{typeInfo.label}</Badge>
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditModal(holiday)}
                              className="text-blue-600 hover:text-blue-700"
                              disabled={loading}
                              data-testid={`edit-holiday-${holiday.id || holiday.date}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteConfirm(holiday)}
                              className="text-red-600 hover:text-red-700"
                              disabled={loading}
                              data-testid={`delete-holiday-${holiday.id || holiday.date}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <p className="text-center text-slate-500 py-4">No hay festivos configurados para {selectedYear}</p>
          )}
        </CardContent>
      </Card>

      {/* Custom Holidays Section */}
      {customHolidays.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Festivos Personalizados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Municipio</TableHead>
                  {isAdmin && <TableHead className="text-right">Acciones</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {customHolidays.map((holiday) => (
                  <TableRow key={holiday.id}>
                    <TableCell>{formatDate(holiday.date)}</TableCell>
                    <TableCell>{holiday.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {getMunicipalityDisplay(holiday.municipality)}
                      </Badge>
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(holiday)}
                            className="text-blue-600 hover:text-blue-700"
                            disabled={loading}
                            data-testid={`edit-custom-holiday-${holiday.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirm(holiday)}
                            className="text-red-600 hover:text-red-700"
                            disabled={loading}
                            data-testid={`delete-custom-holiday-${holiday.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Add Holiday Dialog */}
      <Dialog open={showAddForm} onOpenChange={(open) => { setShowAddForm(open); if (!open) resetForm(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Añadir Festivo Local</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddHoliday} className="space-y-4">
            <div>
              <Label htmlFor="holiday-date">Fecha</Label>
              <Input
                id="holiday-date"
                type="date"
                value={newHoliday.date}
                onChange={(e) => setNewHoliday({...newHoliday, date: e.target.value})}
                data-testid="add-holiday-date"
                required
              />
            </div>
            <div>
              <Label htmlFor="holiday-name">Nombre del Festivo</Label>
              <Input
                id="holiday-name"
                value={newHoliday.name}
                onChange={(e) => setNewHoliday({...newHoliday, name: e.target.value})}
                placeholder="Ej: Feria Local"
                data-testid="add-holiday-name"
                required
              />
            </div>
            
            {/* Holiday Scope Selection */}
            <div>
              <Label>Ámbito del festivo</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="holidayScope"
                    value="global"
                    checked={holidayScope === 'global'}
                    onChange={() => setHolidayScope('global')}
                    className="w-4 h-4 text-indigo-600"
                  />
                  <span className="text-sm">
                    <span className="font-medium">Global</span>
                    <span className="text-slate-500 ml-1">(aplica a todos)</span>
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="holidayScope"
                    value="local"
                    checked={holidayScope === 'local'}
                    onChange={() => setHolidayScope('local')}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">
                    <span className="font-medium">Local</span>
                    <span className="text-slate-500 ml-1">(solo una población)</span>
                  </span>
                </label>
              </div>
            </div>
            
            {/* Municipality selection - only show if scope is local */}
            {holidayScope === 'local' && (
              <div>
                <Label htmlFor="municipality">Municipio</Label>
                <div className="space-y-2">
                  <Select 
                    value={useCustomMunicipality ? 'custom' : newHoliday.municipality} 
                    onValueChange={(value) => {
                      if (value === 'custom') {
                        setUseCustomMunicipality(true);
                        setNewHoliday({...newHoliday, municipality: ''});
                      } else {
                        setUseCustomMunicipality(false);
                        setNewHoliday({...newHoliday, municipality: value});
                      }
                    }}
                  >
                    <SelectTrigger data-testid="add-holiday-municipality-select">
                      <SelectValue placeholder="Selecciona un municipio" />
                    </SelectTrigger>
                    <SelectContent>
                      {getAllMunicipalities().map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                      <SelectItem value="custom">+ Añadir nuevo municipio</SelectItem>
                    </SelectContent>
                  </Select>
                  {useCustomMunicipality && (
                    <Input
                      placeholder="Nombre del nuevo municipio"
                      value={customMunicipalityName}
                      onChange={(e) => setCustomMunicipalityName(e.target.value)}
                      data-testid="add-holiday-custom-municipality"
                      className="mt-2"
                    />
                  )}
                </div>
              </div>
            )}
            
            {holidayScope === 'global' && (
              <div className="bg-indigo-50 p-3 rounded-lg text-sm text-indigo-700">
                <strong>Festivo Global:</strong> Este festivo se aplicará a TODOS los empleados, independientemente de su población asignada.
              </div>
            )}
            
            <div className="flex gap-2">
              <Button type="submit" disabled={loading} className="flex-1" data-testid="add-holiday-submit">
                {loading ? 'Añadiendo...' : 'Añadir Festivo'}
              </Button>
              <Button type="button" variant="outline" onClick={() => { setShowAddForm(false); resetForm(); }}>
                Cancelar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Holiday Dialog */}
      <Dialog open={showEditForm} onOpenChange={(open) => { setShowEditForm(open); if (!open) { setEditingHoliday(null); resetForm(); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Festivo {editingHoliday?.is_custom ? 'Local' : ''}</DialogTitle>
          </DialogHeader>
          {editingHoliday && (
            <form onSubmit={handleEditHoliday} className="space-y-4">
              <div>
                <Label htmlFor="edit-holiday-date">Fecha</Label>
                <Input
                  id="edit-holiday-date"
                  type="date"
                  value={editingHoliday.date}
                  onChange={(e) => setEditingHoliday({...editingHoliday, date: e.target.value})}
                  data-testid="edit-holiday-date"
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-holiday-name">Nombre del Festivo</Label>
                <Input
                  id="edit-holiday-name"
                  value={editingHoliday.name}
                  onChange={(e) => setEditingHoliday({...editingHoliday, name: e.target.value})}
                  placeholder="Ej: Feria Local"
                  data-testid="edit-holiday-name"
                  required
                />
              </div>
              {editingHoliday.is_custom && (
                <div>
                  <Label htmlFor="edit-municipality">Municipio</Label>
                  <div className="space-y-2">
                    <Select 
                      value={useCustomMunicipality ? 'custom' : editingHoliday.municipality} 
                      onValueChange={(value) => {
                        if (value === 'custom') {
                          setUseCustomMunicipality(true);
                          setEditingHoliday({...editingHoliday, municipality: ''});
                        } else {
                          setUseCustomMunicipality(false);
                          setEditingHoliday({...editingHoliday, municipality: value});
                          setCustomMunicipalityName('');
                        }
                      }}
                    >
                      <SelectTrigger data-testid="edit-holiday-municipality-select">
                        <SelectValue placeholder="Selecciona un municipio" />
                      </SelectTrigger>
                      <SelectContent>
                        {getAllMunicipalities().map(m => (
                          <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                        ))}
                        <SelectItem value="custom">+ Añadir nuevo municipio</SelectItem>
                      </SelectContent>
                    </Select>
                    {useCustomMunicipality && (
                      <Input
                        placeholder="Nombre del municipio"
                        value={customMunicipalityName}
                        onChange={(e) => setCustomMunicipalityName(e.target.value)}
                        data-testid="edit-holiday-custom-municipality"
                        className="mt-2"
                      />
                    )}
                  </div>
                </div>
              )}
              {!editingHoliday.is_custom && (
                <p className="text-sm text-slate-500 bg-slate-50 p-3 rounded">
                  Este es un festivo {editingHoliday.type === 'national' ? 'nacional' : editingHoliday.type === 'regional' ? 'autonómico' : 'local'}. 
                  Puedes modificar su fecha y nombre.
                </p>
              )}
              <div className="flex gap-2">
                <Button type="submit" disabled={loading} className="flex-1" data-testid="edit-holiday-submit">
                  {loading ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setShowEditForm(false); setEditingHoliday(null); resetForm(); }}>
                  Cancelar
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Eliminación</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            ¿Estás seguro de que deseas eliminar el festivo &quot;{deleteConfirm?.name}&quot; del {deleteConfirm && formatDate(deleteConfirm.date)}?
          </p>
          {deleteConfirm && !deleteConfirm.is_custom && deleteConfirm.type !== 'custom' && (
            <p className="text-amber-600 text-sm mt-2">
              Nota: Este es un festivo predefinido. Se ocultará de la lista pero podrás restaurarlo más adelante si lo necesitas.
            </p>
          )}
          <div className="flex gap-2 mt-4">
            <Button 
              variant="destructive" 
              onClick={() => handleDeleteHoliday(deleteConfirm)}
              disabled={loading}
              className="flex-1"
            >
              {loading ? 'Eliminando...' : 'Eliminar'}
            </Button>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-green-600" />
              Importar Festivos desde Excel
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 p-4 rounded-lg space-y-3">
              <h4 className="font-medium">Instrucciones:</h4>
              <ol className="text-sm text-slate-600 list-decimal list-inside space-y-1">
                <li>Descarga la plantilla Excel</li>
                <li>Rellena los festivos (fecha, nombre, ámbito)</li>
                <li>Ámbito: "global" para todos, o nombre de población</li>
                <li>Guarda el archivo y súbelo aquí</li>
              </ol>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleDownloadTemplate} className="flex-1">
                <Download className="h-4 w-4 mr-2" />
                Descargar Plantilla {selectedYear + 1}
              </Button>
            </div>
            
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImportFile}
                ref={fileInputRef}
                className="hidden"
                id="excel-upload"
              />
              <label 
                htmlFor="excel-upload" 
                className="cursor-pointer flex flex-col items-center gap-2"
              >
                <Upload className="h-8 w-8 text-slate-400" />
                <span className="text-sm text-slate-600">
                  {loading ? 'Importando...' : 'Haz clic para seleccionar archivo Excel'}
                </span>
              </label>
            </div>
            
            {importResult && (
              <div className={`p-3 rounded-lg text-sm ${importResult.error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                {importResult.error ? (
                  <p>{importResult.error}</p>
                ) : (
                  <div>
                    <p className="font-medium">Importación completada:</p>
                    <ul className="mt-1 list-disc list-inside">
                      <li>Festivos importados: {importResult.inserted}</li>
                      {importResult.skipped_duplicates > 0 && (
                        <li>Omitidos (ya existían): {importResult.skipped_duplicates}</li>
                      )}
                    </ul>
                    {importResult.errors && importResult.errors.length > 0 && (
                      <div className="mt-2 text-amber-700">
                        <p className="font-medium">Errores encontrados:</p>
                        <ul className="list-disc list-inside">
                          {importResult.errors.map((err, idx) => (
                            <li key={idx}>{err}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => { setShowImportDialog(false); setImportResult(null); }}>
                Cerrar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HolidayManagement;
